import { generateCsrfToken } from '../../utils/generateCsrfToken';
import { getFileExtension } from '../../utils/getFileExtension';
import { loadImage } from '../../utils/loadImage';
import type { MediaModel } from './types';

class FileUploadError extends Error {
    constructor(message: string = 'Не удалось загрузить файл') {
        super(message);
        this.name = 'FileUploadError';
    }
}

class FileMetaError extends Error {
    constructor(message: string = 'Не удалось загрузить мета информацию') {
        super(message);
        this.name = 'FileMetaError';
    }
}

export enum FileType {
    IMAGE = 'image',
    VIDEO = 'video',
    UNKNOWN = 'unknown',
}

export async function convertToBlob(file: File): Promise<Blob> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            if (!(e.target?.result instanceof ArrayBuffer)) {
                return reject(new FileUploadError());
            }

            const blob = new Blob([new Uint8Array(e.target.result)], {
                type: 'application/octet-stream',
            });

            resolve(blob);
        };

        reader.onerror = (error) => {
            reject(error);
        };

        reader.readAsArrayBuffer(file);
    });
}

function getFileType(file: File): FileType {
    if (file.type.startsWith('image')) {
        return FileType.IMAGE;
    }

    if (file.type.startsWith('video')) {
        return FileType.VIDEO;
    }

    return FileType.UNKNOWN;
}

interface MediaSizes {
    width: number;
    height: number;
}

export async function uploadBlob({
    blob,
    fileType,
    fileExtension,
    mediaSizes,
}: {
    blob: Blob | null;
    fileType: string;
    fileExtension?: string;
    mediaSizes?: MediaSizes;
}): Promise<MediaModel> {
    return new Promise((resolve, reject) => {
        if (!blob) {
            throw new FileUploadError();
        }

        const xhr = new XMLHttpRequest();
        const formData = new FormData();

        xhr.addEventListener('loadend', () => {
            if (xhr.readyState === 4 && xhr.status >= 200 && xhr.status < 300) {
                return resolve({ type: fileType, ...JSON.parse(xhr.responseText) });
            }

            reject(new FileUploadError());
        });

        xhr.addEventListener('error', () => {
            reject(new FileUploadError());
        });

        generateCsrfToken()
            .then((csrfToken) => {
                const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
                xhr.open('POST', `${apiBase}/media`, true);

                xhr.withCredentials = true;

                xhr.setRequestHeader('X-Csrf-Token', csrfToken);

                formData.append('type', fileType);
                formData.append('content', blob);

                if (fileExtension) {
                    formData.append('extension', fileExtension);
                }

                if (mediaSizes) {
                    formData.append('width', mediaSizes.width.toString());
                    formData.append('height', mediaSizes.height.toString());
                }

                xhr.send(formData);
            })
            .catch(reject);
    });
}

export async function uploadFile(file: File, mediaSizes?: MediaSizes): Promise<MediaModel> {
    const fileType = getFileType(file);
    const fileExtension = getFileExtension(file);

    if (fileType === FileType.UNKNOWN) {
        throw new FileUploadError();
    }

    const blob = await convertToBlob(file);

    return uploadBlob({ blob, fileType, fileExtension, mediaSizes });
}

export async function cropImage(imageSrc: string, area: Inspire.Area): Promise<Blob | null> {
    const image = await loadImage(imageSrc);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
        return null;
    }

    canvas.width = image.width;
    canvas.height = image.height;

    context.drawImage(image, 0, 0);

    const data = context.getImageData(area.x, area.y, area.width, area.height);

    canvas.width = area.width;
    canvas.height = area.height;

    context.putImageData(data, 0, 0);

    const [, base64] = canvas.toDataURL('image/jpeg').split(',');
    const decoded = atob(base64);
    const arrayBuffer = new ArrayBuffer(decoded.length);
    const view = new Uint8Array(arrayBuffer);

    for (let i = 0; i < decoded.length; i++) {
        view[i] = decoded.charCodeAt(i) & 0xff;
    }

    return new Blob([arrayBuffer], { type: 'application/octet-stream' });
}

type ImageFileMeta = {
    url: string;
    width: number;
    height: number;
    size: number;
};

export async function getImageFileMeta(file: File): Promise<ImageFileMeta> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const element = document.createElement('img');

        element.setAttribute('src', url);
        element.addEventListener('load', () => {
            resolve({
                url,
                width: element.naturalWidth,
                height: element.naturalHeight,
                size: file.size,
            });
        });

        element.addEventListener('error', () => reject(new FileMetaError()));
    });
}

type VideoFileMeta = {
    url: string;
    width: number;
    height: number;
    duration: number;
    size: number;
};

export async function getVideoFileMeta(file: File): Promise<VideoFileMeta> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement('video');

        video.addEventListener('loadedmetadata', () => {
            const { videoWidth, videoHeight, duration } = video;

            resolve({
                url,
                size: file.size,
                width: videoWidth,
                height: videoHeight,
                duration,
            });
        });

        video.addEventListener('error', () => reject(new FileMetaError()));
        video.setAttribute('src', '');
        video.setAttribute('preload', 'metadata');
        video.setAttribute('src', url);
        video.load();
    });
}
